import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { Button, CircularProgress } from '@occc-app/design-system';

import { KioskPreview } from '../../assets-details-page/components/kiosk-preview.component';

type AspectRatio = '4x3' | '1x1' | '9x16';

type AssetImageUploadContentProps = {
  previewUrl: string | null;
  aspectRatio: AspectRatio;
  style: React.CSSProperties;
  onRemove: () => void;
  isDragActive: boolean;
  onOpen: () => void;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  fieldName: string;
  isUploading?: boolean;
  disabled?: boolean;
};

const deleteButtonStyles = {
  position: 'absolute',
  width: '32px',
  height: '32px',
  right: '8px',
  top: '8px',
  borderRadius: '42.6667px',
  backgroundColor: '#FAEDEA',
  zIndex: 10,
  '&:hover': {
    backgroundColor: '#FAEDEA',
  },
  '& .MuiSvgIcon-root': {
    color: '#F44336',
    width: '19.2px',
    height: '19.2px',
  },
} as const;

const getBorderRadius = (aspectRatio: AspectRatio): string => {
  return aspectRatio === '9x16' ? '4px' : '9px';
};

const LoadingOverlay = ({ borderRadius }: { borderRadius: string }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius,
      zIndex: 5,
    }}
  >
    <CircularProgress size={32} aria-label='Uploading image' />
    <Typography variant='body2' color='text.secondary'>
      Uploading...
    </Typography>
  </Box>
);

export const AssetImageUploadContent = ({
  previewUrl,
  aspectRatio,
  style,
  onRemove,
  isDragActive,
  onOpen,
  inputProps,
  fieldName,
  isUploading = false,
  disabled = false,
}: AssetImageUploadContentProps) => {
  const borderRadius = getBorderRadius(aspectRatio);

  if (previewUrl) {
    // For 1x1 images, use KioskPreview component
    if (aspectRatio === '1x1') {
      return (
        <Box width={style.width} height={style.height}>
          <KioskPreview imageUrl={previewUrl} />
          {isUploading && <LoadingOverlay borderRadius={borderRadius} />}
          {!disabled && (
            <IconButton onClick={onRemove} sx={deleteButtonStyles} disabled={isUploading}>
              <DeleteForeverIcon />
            </IconButton>
          )}
        </Box>
      );
    }

    // For other aspect ratios, use standard image preview
    return (
      <Box width={style.width} height={style.height}>
        <Box
          component='img'
          src={previewUrl}
          alt={`${aspectRatio} image preview`}
          sx={{
            ...style,
            width: '100%',
            height: '100%',
          }}
        />
        {isUploading && <LoadingOverlay borderRadius={borderRadius} />}
        {!disabled && (
          <IconButton onClick={onRemove} sx={deleteButtonStyles} disabled={isUploading}>
            <DeleteForeverIcon />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: isDragActive ? 'action.hover' : 'common.white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        textAlign: 'center',
        width: style.width,
        height: style.height,
        borderRadius,
        border: isDragActive ? '2px dashed' : 'none',
        borderColor: isDragActive ? 'primary.main' : 'grey.400',
        transition: 'all 0.2s',
      }}
    >
      {isUploading ? (
        <>
          <CircularProgress size={32} aria-label='Uploading image' />
          <Typography variant='body2' color='text.secondary'>
            Uploading...
          </Typography>
        </>
      ) : (
        <>
          <UploadIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          <Typography variant='body2' color='text.secondary'>
            Drag and Drop
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            OR
          </Typography>
          <Button variant='contained' size='small' onClick={onOpen} disabled={disabled}>
            Browse Files
          </Button>
        </>
      )}
      <input {...inputProps} name={fieldName} disabled={disabled} />
    </Box>
  );
};
